import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Loader2, CheckCircle2 } from "lucide-react";
import { SERVICES } from "@/data/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatApiErrorDetail } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  patient_name: "",
  phone: "",
  email: "",
  service: "",
  preferred_date: "",
  preferred_time: "",
  message: "",
};

export const BookingForm = () => {
  const [form, setForm] = useState(empty);
  const [allergyConfirmed, setAllergyConfirmed] = useState(false);
  const [showMedical, setShowMedical] = useState(false);
  const [medicalNotes, setMedicalNotes] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.patient_name || !form.phone || !form.service || !form.preferred_time) {
      toast.error("Please fill in name, phone, service and preferred time.");
      return;
    }
    if (!allergyConfirmed) {
      toast.error("Please confirm the allergy declaration to proceed.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("patient_name", form.patient_name);
      fd.append("phone", form.phone);
      fd.append("email", form.email || "");
      fd.append("service", form.service);
      fd.append("preferred_date", form.preferred_date || "");
      fd.append("preferred_time", form.preferred_time);
      fd.append("message", form.message || "");
      fd.append("allergy_confirmed", allergyConfirmed ? "true" : "false");
      fd.append("medical_notes", showMedical ? medicalNotes : "");
      if (file) fd.append("file", file);

      await axios.post(`${API}/appointments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(true);
      toast.success("Appointment requested! A WhatsApp confirmation is on its way.");
      setForm(empty);
      setAllergyConfirmed(false);
      setShowMedical(false);
      setMedicalNotes("");
      setFile(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="booking-success"
        className="bg-white rounded-2xl border border-sage-200 shadow-sm p-10 text-center"
      >
        <CheckCircle2 className="mx-auto text-sage-400 mb-4" size={56} />
        <h3 className="font-heading text-3xl text-sage-900 mb-2">Request Received</h3>
        <p className="text-sage-600 mb-6">
          Thank you! We've received your appointment request and sent a WhatsApp confirmation
          with your details.
        </p>
        <Button
          data-testid="book-another-btn"
          onClick={() => setDone(false)}
          className="rounded-full bg-sage-800 hover:bg-sage-900"
        >
          Book Another Appointment
        </Button>
      </div>
    );
  }

  return (
    <form
      data-testid="booking-form"
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl border border-sage-200 shadow-sm p-6 sm:p-8 space-y-5"
    >
      <div>
        <h3 className="font-heading text-3xl text-sage-900">Book an Appointment</h3>
        <p className="text-sage-500 text-sm mt-1">
          Fill in your details and we'll confirm your visit shortly.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="patient_name">Full Name *</Label>
          <Input
            id="patient_name"
            data-testid="input-name"
            value={form.patient_name}
            onChange={set("patient_name")}
            placeholder="Your name"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="phone">Phone (WhatsApp) *</Label>
          <Input
            id="phone"
            data-testid="input-phone"
            value={form.phone}
            onChange={set("phone")}
            placeholder="+91 9XXXXXXXXX"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          data-testid="input-email"
          value={form.email}
          onChange={set("email")}
          placeholder="you@example.com"
        />
      </div>

      <div className="space-y-1.5">
        <Label>Dental Service *</Label>
        <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
          <SelectTrigger data-testid="select-service">
            <SelectValue placeholder="Select a service" />
          </SelectTrigger>
          <SelectContent>
            {SERVICES.map((s) => (
              <SelectItem key={s} value={s} data-testid={`service-option-${s.replace(/\s+/g, "-").toLowerCase()}`}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="preferred_date">Preferred Date</Label>
          <Input
            id="preferred_date"
            type="date"
            data-testid="input-date"
            value={form.preferred_date}
            onChange={set("preferred_date")}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="preferred_time">Preferred Appointment Time *</Label>
          <Input
            id="preferred_time"
            type="time"
            data-testid="input-time"
            value={form.preferred_time}
            onChange={set("preferred_time")}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="file">Attach X-ray or Photo</Label>
        <label
          htmlFor="file"
          data-testid="file-upload-label"
          className="flex items-center gap-3 border border-dashed border-sage-300 rounded-lg px-4 py-3 cursor-pointer hover:bg-sage-50 transition-colors"
        >
          <Upload size={18} className="text-sage-400" />
          <span className="text-sm text-sage-600 truncate">
            {file ? file.name : "Click to upload (JPG, PNG, PDF)"}
          </span>
        </label>
        <input
          id="file"
          type="file"
          accept=".jpg,.jpeg,.png,.webp,.pdf"
          data-testid="input-file"
          className="hidden"
          onChange={(e) => setFile(e.target.files[0] || null)}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="message">Additional Notes</Label>
        <Textarea
          id="message"
          data-testid="input-message"
          value={form.message}
          onChange={set("message")}
          placeholder="Anything you'd like us to know?"
          rows={2}
        />
      </div>

      <div className="rounded-lg bg-sage-50 border border-sage-200 p-4 space-y-3">
        <div className="flex items-start gap-3">
          <Checkbox
            id="allergy"
            data-testid="allergy-checkbox"
            checked={allergyConfirmed}
            onCheckedChange={(v) => setAllergyConfirmed(!!v)}
            className="mt-0.5"
          />
          <Label htmlFor="allergy" className="text-sm text-sage-700 leading-snug cursor-pointer">
            I confirm that I have no known allergies to dental materials or anesthesia. *
          </Label>
        </div>
        <button
          type="button"
          data-testid="toggle-medical-notes"
          onClick={() => setShowMedical(!showMedical)}
          className="text-xs font-600 text-sage-600 underline underline-offset-2 hover:text-sage-800"
        >
          {showMedical ? "Hide" : "Need to add medical conditions / allergies?"}
        </button>
        {showMedical && (
          <div className="space-y-1.5 pt-1">
            <Label htmlFor="medical" className="text-xs text-sage-600">
              Please list any medical conditions or allergies we should be aware of
            </Label>
            <Textarea
              id="medical"
              data-testid="input-medical-notes"
              value={medicalNotes}
              onChange={(e) => setMedicalNotes(e.target.value)}
              rows={3}
              placeholder="e.g. penicillin allergy, diabetes, hypertension..."
            />
          </div>
        )}
      </div>

      <Button
        type="submit"
        data-testid="submit-booking-btn"
        disabled={loading}
        className="w-full rounded-full bg-gold hover:bg-gold-dark text-sage-900 font-700 h-12 text-base"
      >
        {loading ? <Loader2 className="animate-spin" size={18} /> : "Request Appointment"}
      </Button>
    </form>
  );
};
